import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet";

// Component imports
import PurchaseInput from "./components/PurchaseInput";
import EnhancedGuiltGauge from "./components/EnhancedGuiltGauge"; 
import Dashboard from "./components/Dashboard";
import Header from "./components/Header";
import Footer from "./components/Footer";
import SpendingGoals from "./components/SpendingGoals"; 
import ShoppingInsights from "./components/ShoppingInsights"; 
import Challenges from "./components/Challenges"; 
import UserProfile from "./components/UserProfile";
import SEOStructuredData from "./components/SEOStructuredData";
import EnhancedSEOMeta from './components/EnhancedSEOMeta';
import Breadcrumb from "./components/Breadcrumb";
import RelatedContent from "./components/RelatedContent";
import { LoadingProvider } from "./components/LoadingProvider";
import LazyComponent from "./components/LazyComponent";
import ShareableInfographics from "./components/ShareableInfographics";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Quiz imports
import QuizHub from "./components/QuizHub";
import ShopaholicTest from "./quizzes/ShopaholicTest";
import ShoppingPersonalityQuiz from "./quizzes/ShoppingPersonalityQuiz";
import FinancialValuesQuiz from "./quizzes/FinancialValuesQuiz";
import EmotionalSpendingTriggerQuiz from "./quizzes/EmotionalSpendingTriggerQuiz";

// Landing page imports
import SEOLandingPage from "./landing-pages/SEOLandingPage";
import ShoppingGuiltBlogPost from "./landing-pages/ShoppingGuiltBlogPost";
import ShopaholicLandingPage from "./landing-pages/ShopaholicLandingPage";
import FashionShoppingGuiltLanding from "./landing-pages/FashionShoppingGuiltLanding";
import TechShoppingGuiltLanding from "./landing-pages/TechShoppingGuiltLanding";
import LuxuryShoppingGuiltLanding from "./landing-pages/LuxuryShoppingGuiltLanding";
import HomeDecorShoppingRemorseLanding from "./landing-pages/HomeDecorShoppingRemorseLanding";
import HolidayShoppingGuiltLanding from "./landing-pages/HolidayShoppingGuiltLanding";
import BlackFridayRemorseLanding from "./landing-pages/BlackFridayRemorseLanding";
import BirthdaySplurgeLanding from "./landing-pages/BirthdaySplurgeLanding";
import StudentShoppingGuiltLanding from "./landing-pages/StudentShoppingGuiltLanding";
import ParentShoppingGuiltLanding from "./landing-pages/ParentShoppingGuiltLanding";
import RetirementShoppingLanding from "./landing-pages/RetirementShoppingLanding";
import DebtShoppingLandingPage from "./landing-pages/DebtShoppingLandingPage";
import BudgetShoppingLandingPage from "./landing-pages/BudgetShoppingLandingPage";
import PostPaydayLandingPage from "./landing-pages/PostPaydayLandingPage";
import EmotionalShoppingTrackerLanding from "./landing-pages/EmotionalShoppingTrackerLanding";
import ImpulseBuyingLandingPage from "./landing-pages/ImpulseBuyingLandingPage";
import FOMOShoppingLandingPage from "./landing-pages/FOMOShoppingLandingPage";
import LateNightShoppingLanding from "./landing-pages/LateNightShoppingLanding";
import NoSpendChallengeLanding from "./landing-pages/NoSpendChallengeLanding";
import ShoppingDetoxLanding from "./landing-pages/ShoppingDetoxLanding";
import ShoppingAddictionRecoveryStories from "./landing-pages/ShoppingAddictionRecoveryStories";
import ShoppingGuiltVsFinancialAnxietyLanding from "./landing-pages/ShoppingGuiltVsFinancialAnxietyLanding";
import RetailTherapyVsAddictionLanding from "./landing-pages/RetailTherapyVsAddictionLanding";
import BuyersRemorseTimelineLanding from "./landing-pages/BuyersRemorseTimelineLanding";
import BNPLLandingPage from "./landing-pages/BNPLLandingPage";

// Import the simplified shop component
const MindfulShopComponent = React.lazy(() => import('./components/MindfulShopComponent'));

const App = () => {
  // State for current view
  const [currentView, setCurrentView] = useState(() => {
    // Check URL path first for specific pages
    const path = window.location.pathname;
    if (path === "/shopping-addiction") return "shopaholic";
    if (path === "/shopping-addiction-test") return "shopaholic-test";
    if (path === "/fashion-shopping-guilt") return "fashion-guilt";
    if (path === "/tech-shopping-guilt") return "tech-guilt";
    if (path === "/luxury-shopping-guilt") return "luxury-guilt";
    if (path === "/home-decor-remorse") return "home-decor-remorse";
    if (path === "/holiday-shopping-guilt") return "holiday-guilt";
    if (path === "/black-friday-remorse") return "black-friday-remorse";
    if (path === "/birthday-splurge") return "birthday-splurge";
    if (path === "/student-shopping-guilt") return "student-guilt";
    if (path === "/parent-shopping-guilt") return "parent-guilt";
    if (path === "/retirement-shopping") return "retirement-shopping";
    if (path === "/debt-shopping") return "debt-shopping";
    if (path === "/budget-shopping") return "budget-shopping";
    if (path === "/post-payday-shopping") return "post-payday";
    if (path === "/emotional-shopping-tracker") return "emotional-shopping";
    if (path === "/impulse-buying") return "impulse-buying";
    if (path === "/fomo-shopping") return "fomo-shopping";
    if (path === "/late-night-shopping") return "late-night-shopping";
    if (path === "/no-spend-challenge") return "no-spend-challenge";
    if (path === "/shopping-detox") return "shopping-detox";
    if (path === "/shopping-addiction-stories") return "addiction-stories";
    if (path === "/shopping-guilt-vs-anxiety") return "guilt-vs-anxiety";
    if (path === "/retail-therapy-vs-addiction") return "retail-therapy";
    if (path === "/buyers-remorse-timeline") return "remorse-timeline";
    if (path === "/shareable-infographics") return "shareable-infographics";
    if (path === "/buy-now-pay-later") return "buy-now-pay-later";
    
    // Check URL parameters for other views
    const urlParams = new URLSearchParams(window.location.search);
    const view = urlParams.get('view');
    if (view === 'blog') return "blog";
    if (view === 'dashboard') return "dashboard";
    if (view === 'insights') return "insights";
    if (view === 'challenges') return "challenges";
    if (view === 'goals') return "goals";
    if (view === 'shop') return "shop";
    
    return "landing"; // Default to landing page
  });
  
  // State for purchase history
  const [purchaseHistory, setPurchaseHistory] = useState(() => {
    const saved = localStorage.getItem("guiltGaugePurchases");
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    // Track page view when currentView changes
    if (typeof window.gtag === 'function') {
      const pagePath = window.location.pathname + window.location.search;
      const pageTitle = getSeoTitle();
      
      // Enhanced event tracking
      window.gtag('config', 'G-2ZRKG21NJX', {
        page_path: pagePath,
        page_title: pageTitle,
        page_location: window.location.href
      });
      
      // Track engagement time
      window.gtag('event', 'page_view', {
        page_title: pageTitle,
        page_path: pagePath,
        send_to: 'G-2ZRKG21NJX'
      });
    }
  }, [currentView]);
  
  // State for current purchase
  const [currentPurchase, setCurrentPurchase] = useState(null);

  // State for theme
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem("guiltGaugeTheme");
    return savedTheme || "mint"; // mint, pink, lavender
  });
  
  // State for light/dark mode
  const [colorTheme, setColorTheme] = useState("light"); // light, dark
  
  // State for tracking recent guilt score for Footer prompts
  const [recentGuiltScore, setRecentGuiltScore] = useState(undefined);
  
  // State for user profile
  const [userProfile, setUserProfile] = useState(() => {
    const savedProfile = localStorage.getItem("guiltGaugeUserProfile");
    return savedProfile ? JSON.parse(savedProfile) : null;
  });
  
  // State for showing user profile modal
  const [showProfileModal, setShowProfileModal] = useState(false);

  useEffect(() => {
  // This function handles mapping URLs to views when the site first loads
  const handleInitialUrlPath = () => {
    const path = window.location.pathname;
    
    // Comprehensive mapping from paths to views
    const pathToViewMap = {
      "/": "landing",
      "/calculate": "input",
      "/results": "results",
      "/dashboard": "dashboard",
      "/insights": "insights",
      "/challenges": "challenges",
      "/goals": "goals",
      "/shop": "shop",
      "/blog": "blog",
      "/quizzes": "quiz-hub",
      "/shopping-addiction": "shopaholic",
      "/shopping-addiction-test": "shopaholic-test",
      "/shopping-personality-quiz": "shopping-personality-quiz",
      "/fashion-shopping-guilt": "fashion-guilt",
      "/tech-shopping-guilt": "tech-guilt",
      "/luxury-shopping-guilt": "luxury-guilt",
      "/home-decor-remorse": "home-decor-remorse",
      "/holiday-shopping-guilt": "holiday-guilt",
      "/black-friday-remorse": "black-friday-remorse",
      "/birthday-splurge": "birthday-splurge",
      "/student-shopping-guilt": "student-guilt",
      "/parent-shopping-guilt": "parent-guilt",
      "/retirement-shopping": "retirement-shopping",
      "/debt-shopping": "debt-shopping",
      "/budget-shopping": "budget-shopping",
      "/post-payday-shopping": "post-payday",
      "/emotional-shopping-tracker": "emotional-shopping",
      "/impulse-buying": "impulse-buying",
      "/fomo-shopping": "fomo-shopping",
      "/late-night-shopping": "late-night-shopping",
      "/no-spend-challenge": "no-spend-challenge",
      "/shopping-detox": "shopping-detox",
      "/shopping-addiction-stories": "addiction-stories",
      "/shopping-guilt-vs-anxiety": "guilt-vs-anxiety",
      "/retail-therapy-vs-addiction": "retail-therapy",
      "/buyers-remorse-timeline": "remorse-timeline",
      "/financial-values-quiz": "financial-values-quiz",
      "/emotional-spending-quiz": "emotional-spending-quiz",
      "/shareable-infographics": "shareable-infographics",
      "buy-now-pay-later": "/buy-now-pay-later",
    };
    
    // Check if path exists in our mapping
    if (pathToViewMap[path]) {
      setCurrentView(pathToViewMap[path]);
    } else {
      // Handle query parameters for backward compatibility
      const urlParams = new URLSearchParams(window.location.search);
      const viewParam = urlParams.get('view');
      
      if (viewParam && viewParam !== currentView) {
        setCurrentView(viewParam);
      }
    }
  };
  
  // Run once on initial load
  handleInitialUrlPath();
  
  // We don't put any dependencies here because we only want this to run once on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Save history to localStorage
  useEffect(() => {
    localStorage.setItem("guiltGaugePurchases", JSON.stringify(purchaseHistory));
  }, [purchaseHistory]);
  
  // Save theme to localStorage
  useEffect(() => {
    localStorage.setItem("guiltGaugeTheme", theme);
    document.body.className = `theme-${theme} ${colorTheme === "dark" ? "dark" : ""}`;
  }, [theme, colorTheme]);
  
  // Save user profile to localStorage
  useEffect(() => {
    if (userProfile) {
      localStorage.setItem("guiltGaugeUserProfile", JSON.stringify(userProfile));
    }
  }, [userProfile]);
  
  // Update URL when view changes (for better SEO)
  useEffect(() => {
    // Update URL without page reload
    const url = new URL(window.location);
    
    // Comprehensive mapping of views to clean paths
    const pathMap = {
      "landing": "/",
      "input": "/calculate",
      "results": "/results",
      "dashboard": "/dashboard",
      "insights": "/insights", 
      "challenges": "/challenges",
      "goals": "/goals",
      "shop": "/shop",
      "blog": "/blog",
      "quiz-hub": "/quizzes",
      "shopaholic": "/shopping-addiction",
      "shopaholic-test": "/shopping-addiction-test",
      "shopping-personality-quiz": "/shopping-personality-quiz",
      "fashion-guilt": "/fashion-shopping-guilt",
      "tech-guilt": "/tech-shopping-guilt",
      "luxury-guilt": "/luxury-shopping-guilt",
      "home-decor-remorse": "/home-decor-remorse",
      "holiday-guilt": "/holiday-shopping-guilt",
      "black-friday-remorse": "/black-friday-remorse",
      "birthday-splurge": "/birthday-splurge",
      "student-guilt": "/student-shopping-guilt",
      "parent-guilt": "/parent-shopping-guilt",
      "retirement-shopping": "/retirement-shopping",
      "debt-shopping": "/debt-shopping",
      "budget-shopping": "/budget-shopping",
      "post-payday": "/post-payday-shopping",
      "emotional-shopping": "/emotional-shopping-tracker",
      "impulse-buying": "/impulse-buying",
      "fomo-shopping": "/fomo-shopping",
      "late-night-shopping": "/late-night-shopping",
      "no-spend-challenge": "/no-spend-challenge",
      "shopping-detox": "/shopping-detox",
      "addiction-stories": "/shopping-addiction-stories",
      "guilt-vs-anxiety": "/shopping-guilt-vs-anxiety",
      "retail-therapy": "/retail-therapy-vs-addiction",
      "remorse-timeline": "/buyers-remorse-timeline",
      "financial-values-quiz": "/financial-values-quiz",
      "emotional-spending-quiz": "/emotional-spending-quiz",
      "shareable-infographics": "/shareable-infographics",
      "buy-now-pay-later": "/buy-now-pay-later",
    };
    
    // Set the pathname to the mapped path or default to home
    url.pathname = pathMap[currentView] || '/';
    
    // Always remove the view parameter
    url.searchParams.delete('view');
    
    window.history.pushState({}, '', url);
    
    // Scroll to top when view changes
    window.scrollTo(0, 0);
  }, [currentView]);
  
  // Function to save a new purchase to history
  const savePurchase = (purchase) => {
    const updatedHistory = [purchase, ...purchaseHistory].slice(0, 50); // Keep only the most recent 50
    setPurchaseHistory(updatedHistory);
    setCurrentPurchase(purchase);
    setCurrentView("results");
    
    // Update recent guilt score for Footer
    setRecentGuiltScore(purchase.guiltScore);
    
    toast.success("Purchase logged! Let's see how guilty you should feel...", {
      position: "top-center",
      autoClose: 2000,
    });
  };
  
  // Function to clear history
  const clearHistory = () => {
    setPurchaseHistory([]);
    toast.info("History cleared", {
      position: "top-center",
      autoClose: 2000,
    });
    setCurrentView("input");
  };
  
  // Function to update user profile
  const updateUserProfile = (profile) => {
    setUserProfile(profile);
    toast.success("Profile updated!", {
      position: "top-center",
      autoClose: 2000,
    });
  };
  
  // Function to get impulse persona after 5 logs
  const getImpulsePersona = () => {
    if (purchaseHistory.length < 5) return null;
    
    // Count most frequent mood
    const moodCounts = purchaseHistory.reduce((acc, purchase) => {
      acc[purchase.mood] = (acc[purchase.mood] || 0) + 1;
      return acc;
    }, {});
    
    const topMood = Object.entries(moodCounts).sort((a, b) => b[1] - a[1])[0][0];
    
    // Calculate average guilt score
    const avgGuilt = purchaseHistory.reduce((sum, p) => sum + p.guiltScore, 0) / purchaseHistory.length;
    
    // Determine persona
    if (topMood === "bored" && avgGuilt > 70) return "Emotional Magpie";
    if (topMood === "celebrating" && avgGuilt < 40) return "Treat Yo'self Champion";
    if (avgGuilt > 60) return "Luxury Justifier";
    if (avgGuilt < 30) return "Mindful Spender";
    return "Discount Daredevil";
  };
  
  // Theme colors
  const themeColors = {
    mint: "bg-gradient-to-br from-emerald-50 via-white to-teal-50 dark:from-dark-900 dark:via-dark-800 dark:to-dark-900",
    pink: "bg-gradient-to-br from-pink-50 via-white to-rose-50 dark:from-dark-900 dark:via-dark-800 dark:to-dark-900",
    lavender: "bg-gradient-to-br from-purple-50 via-white to-indigo-50 dark:from-dark-900 dark:via-dark-800 dark:to-dark-900"
  };
  
  // Dynamic SEO title and description based on current view
  const getSeoTitle = () => {
    switch(currentView) {
      case "landing":
        return "Stop Feeling Guilty After Shopping | Guilt Gauge";
      case "blog":
        return "Why Do We Feel Guilty After Shopping? | Guilt Gauge";
      case "results":
        return "Your Shopping Guilt Score | Guilt Gauge";
      case "dashboard":
        return "Shopping Guilt Dashboard | Guilt Gauge";
      case "insights":
        return "Your Shopping Psychology & Guilt Patterns | Guilt Gauge";
      case "challenges":
        return "Overcome Shopping Guilt Challenges | Guilt Gauge";
      case "shop":
        return "Shop Mindfully | Guilt Gauge";
      case "fashion-guilt":
        return "Overcome Fashion Shopping Guilt | Guilt Gauge";
      case "tech-guilt":
        return "Tech Shopping Guilt Calculator | Guilt Gauge";
      case "luxury-guilt":
        return "Luxury Shopping Guilt Solutions | Guilt Gauge";
      case "home-decor-remorse":
        return "Home Decor Buyer's Remorse Help | Guilt Gauge";
      case "holiday-guilt":
        return "Holiday Shopping Guilt Tracker | Guilt Gauge";
      case "black-friday-remorse":
        return "Black Friday Buyer's Remorse Solutions | Guilt Gauge";
      case "birthday-splurge":
        return "Birthday Splurge Guilt Calculator | Guilt Gauge";
      case "student-guilt":
        return "Student Shopping Guilt Tracker | Guilt Gauge";
      case "parent-guilt":
        return "Parent Shopping Guilt Solutions | Guilt Gauge";
      case "retirement-shopping":
        return "Retirement Shopping Guilt Help | Guilt Gauge";
      case "debt-shopping":
        return "Shopping with Debt Guilt Tracker | Guilt Gauge";
      case "budget-shopping":
        return "Budget Shopping Guilt Solutions | Guilt Gauge";
      case "post-payday":
        return "Post-Payday Shopping Guilt Calculator | Guilt Gauge";
      case "emotional-shopping":
        return "Emotional Shopping Tracker | Guilt Gauge";
      case "impulse-buying":
        return "Impulse Buying Guilt Solutions | Guilt Gauge";
      case "fomo-shopping":
        return "FOMO Shopping Guilt Calculator | Guilt Gauge";
      case "late-night-shopping":
        return "Late Night Shopping Guilt Tracker | Guilt Gauge";
      case "no-spend-challenge":
        return "No-Spend Challenge | Guilt Gauge";
      case "shopping-detox":
        return "Shopping Detox Program | Guilt Gauge";
      case "addiction-stories":
        return "Shopping Addiction Recovery Stories | Guilt Gauge";
      case "guilt-vs-anxiety":
        return "Shopping Guilt vs Financial Anxiety | Guilt Gauge";
      case "retail-therapy":
        return "Retail Therapy vs Shopping Addiction | Guilt Gauge";
      case "remorse-timeline":
        return "Buyer's Remorse Timeline | Guilt Gauge";
      case "shareable-infographics":
        return "Shopping Addiction Infographics | Guilt Gauge";
      default:
        return "Calculate Your Shopping Guilt | Guilt Gauge";
    }
  };
  
  const getSeoDescription = () => {
    switch(currentView) {
      case "landing":
        return "Calculate your shopping guilt score and get personalized advice to shop without regret. Free tool for mindful spending and overcoming buyer's remorse.";
      case "blog":
        return "Learn why we feel guilty after shopping, the psychology behind buyer's remorse, and 7 proven strategies to overcome shopping guilt and regret.";
      case "results":
        return "See your personalized shopping guilt score and get tailored advice to make better purchasing decisions without the regret.";
      case "dashboard":
        return "Track your shopping habits, guilt patterns, and emotional spending triggers with the Guilt Gauge dashboard.";
      case "insights":
        return "Discover your unique shopping personality and understand the psychology behind your spending habits and shopping guilt.";
      case "challenges":
        return "Take on challenges designed to help you overcome shopping guilt, reduce impulse purchases, and develop healthier spending habits.";
      case "shop":
        return "Products that align with mindful spending and help reduce shopping guilt. Recommended tools for better financial habits.";
      case "fashion-guilt":
        return "Track fashion purchases and overcome guilt with our free tool. Get personalized advice for mindful clothing shopping.";
      case "tech-guilt":
        return "Calculate guilt scores for tech purchases. Learn to shop smarter for gadgets and avoid buyer's remorse.";
      case "luxury-guilt":
        return "Manage guilt from luxury purchases with our tracker. Get tailored advice for high-end shopping decisions.";
      case "home-decor-remorse":
        return "Overcome home decor buyer's remorse with our free tool. Track purchases and shop mindfully for your home.";
      case "holiday-guilt":
        return "Track holiday shopping and reduce guilt. Get personalized tips for stress-free festive spending.";
      case "black-friday-remorse":
        return "Avoid Black Friday buyer's remorse with our guilt tracker. Shop deals mindfully and regret-free.";
      case "birthday-splurge":
        return "Calculate guilt for birthday splurges. Get advice to celebrate without buyer's remorse.";
      case "student-guilt":
        return "Track student shopping and reduce guilt. Get budgeting tips for mindful spending on a student budget.";
      case "parent-guilt":
        return "Manage parent shopping guilt with our tracker. Shop for your family without regret.";
      case "retirement-shopping":
        return "Track retirement shopping and avoid guilt. Get advice for mindful spending in retirement.";
      case "debt-shopping":
        return "Shop mindfully while managing debt. Track purchases and reduce guilt with our free tool.";
      case "budget-shopping":
        return "Stick to your budget and shop guilt-free. Track purchases and get personalized budgeting tips.";
      case "post-payday":
        return "Track post-payday shopping and avoid guilt. Get tips for mindful spending after payday.";
      case "emotional-shopping":
        return "Track emotional purchases and understand triggers. Get advice to shop mindfully and reduce guilt.";
      case "impulse-buying":
        return "Overcome impulse buying with our guilt tracker. Get personalized tips for mindful shopping.";
      case "fomo-shopping":
        return "Calculate guilt for FOMO-driven purchases. Shop mindfully and avoid regret with our tool.";
      case "late-night-shopping":
        return "Track late-night shopping and reduce guilt. Get tips for mindful purchasing decisions.";
      case "no-spend-challenge":
        return "Join our no-spend challenge to reduce shopping guilt and build mindful spending habits.";
      case "shopping-detox":
        return "Start a shopping detox to overcome guilt and compulsive buying. Get personalized recovery tips.";
      case "addiction-stories":
        return "Read inspiring shopping addiction recovery stories and learn strategies to shop mindfully.";
      case "guilt-vs-anxiety":
        return "Understand shopping guilt vs financial anxiety. Get tools to manage both and shop smarter.";
      case "retail-therapy":
        return "Learn the difference between retail therapy and shopping addiction. Get tips for healthy spending.";
      case "remorse-timeline":
        return "Explore the buyer's remorse timeline and learn how to shop without regret.";
      case "shareable-infographics":
        return "Easy to share infographics regarding shopping addiction and how to beat it.";
      default:
        return "Track your purchases, calculate your shopping guilt score, and get personalized advice to shop without regret.";
    }
  };
  
  return (
    <LoadingProvider>
      <div className={`min-h-screen ${themeColors[theme]} text-gray-800 dark:text-gray-200 p-4 flex flex-col items-center`}>
        
        <SEOStructuredData 
          currentView={currentView} 
          currentPurchase={currentPurchase} 
        />
        
        <EnhancedSEOMeta 
          currentView={currentView} 
          purchaseData={currentPurchase} 
          userProfile={userProfile} 
        />
        
        <Header 
          theme={theme} 
          setTheme={setTheme} 
          colorTheme={colorTheme} 
          setColorTheme={setColorTheme}
          currentView={currentView} 
          setCurrentView={setCurrentView}
          showProfileModal={() => setShowProfileModal(true)}
          userProfile={userProfile}
        />

        <Breadcrumb
          currentView={currentView}
          setCurrentView={setCurrentView}
        />
        
        <main className="w-full max-w-md mx-auto mb-16">
          <AnimatePresence mode="wait">
            {currentView === "landing" && (
              <motion.div
                key="landing"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <SEOLandingPage 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
  
            {currentView === "shopaholic" && (
              <motion.div
                key="shopaholic"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ShopaholicLandingPage 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "blog" && (
              <motion.div
                key="blog"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ShoppingGuiltBlogPost 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                  setCurrentView={setCurrentView}
                />
              </motion.div>
            )}
            
            {currentView === "fashion-guilt" && (
              <motion.div
                key="fashion-guilt"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <FashionShoppingGuiltLanding 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "tech-guilt" && (
              <motion.div
                key="tech-guilt"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <TechShoppingGuiltLanding 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "luxury-guilt" && (
              <motion.div
                key="luxury-guilt"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <LuxuryShoppingGuiltLanding 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "home-decor-remorse" && (
              <motion.div
                key="home-decor-remorse"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <HomeDecorShoppingRemorseLanding 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "holiday-guilt" && (
              <motion.div
                key="holiday-guilt"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <HolidayShoppingGuiltLanding 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "black-friday-remorse" && (
              <motion.div
                key="black-friday-remorse"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <BlackFridayRemorseLanding 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "birthday-splurge" && (
              <motion.div
                key="birthday-splurge"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <BirthdaySplurgeLanding 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "student-guilt" && (
              <motion.div
                key="student-guilt"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <StudentShoppingGuiltLanding 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "parent-guilt" && (
              <motion.div
                key="parent-guilt"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ParentShoppingGuiltLanding 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "retirement-shopping" && (
              <motion.div
                key="retirement-shopping"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <RetirementShoppingLanding 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "debt-shopping" && (
              <motion.div
                key="debt-shopping"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <DebtShoppingLandingPage 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "budget-shopping" && (
              <motion.div
                key="budget-shopping"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <BudgetShoppingLandingPage 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "post-payday" && (
              <motion.div
                key="post-payday"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <PostPaydayLandingPage 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "emotional-shopping" && (
              <motion.div
                key="emotional-shopping"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <EmotionalShoppingTrackerLanding 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "impulse-buying" && (
              <motion.div
                key="impulse-buying"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ImpulseBuyingLandingPage 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "fomo-shopping" && (
              <motion.div
                key="fomo-shopping"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <FOMOShoppingLandingPage 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "late-night-shopping" && (
              <motion.div
                key="late-night-shopping"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <LateNightShoppingLanding 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "no-spend-challenge" && (
              <motion.div
                key="no-spend-challenge"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <NoSpendChallengeLanding 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "shopping-detox" && (
              <motion.div
                key="shopping-detox"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ShoppingDetoxLanding 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
  
            {currentView === "quiz-hub" && (
              <motion.div
                key="quiz-hub"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <QuizHub 
                  theme={theme}
                  setCurrentView={setCurrentView}
                />
              </motion.div>
            )}
            
            {currentView === "shopaholic-test" && (
              <motion.div
                key="shopaholic-test"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ShopaholicTest 
                  theme={theme}
                  goToInput={() => setCurrentView("input")}
                />
              </motion.div>
            )}
  
            {currentView === "shopping-personality-quiz" && (
              <motion.div
                key="shopping-personality-quiz"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ShoppingPersonalityQuiz 
                  theme={theme}
                  goToInput={() => setCurrentView("input")}
                />
              </motion.div>
            )}
  
            {currentView === "financial-values-quiz" && (
              <motion.div
                key="financial-values-quiz"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <FinancialValuesQuiz 
                  theme={theme}
                  goToInput={() => setCurrentView("input")}
                />
              </motion.div>
            )}
  
            {currentView === "emotional-spending-quiz" && (
              <motion.div
                key="emotional-spending-quiz"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <EmotionalSpendingTriggerQuiz 
                  theme={theme}
                  goToInput={() => setCurrentView("input")}
                />
              </motion.div>
            )}
            
            {currentView === "addiction-stories" && (
              <motion.div
                key="addiction-stories"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ShoppingAddictionRecoveryStories 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "guilt-vs-anxiety" && (
              <motion.div
                key="guilt-vs-anxiety"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ShoppingGuiltVsFinancialAnxietyLanding 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "retail-therapy" && (
              <motion.div
                key="retail-therapy"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <RetailTherapyVsAddictionLanding 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "remorse-timeline" && (
              <motion.div
                key="remorse-timeline"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <BuyersRemorseTimelineLanding 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {currentView === "input" && (
              <motion.div
                key="input"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <PurchaseInput 
                  savePurchase={savePurchase} 
                  theme={theme} 
                  userProfile={userProfile}
                />
              </motion.div>
            )}
            
            {currentView === "results" && currentPurchase && (
              <motion.div
                key="results"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <EnhancedGuiltGauge 
                  purchase={currentPurchase}
                  purchaseHistory={purchaseHistory}
                  goToInput={() => setCurrentView("input")}
                  goToDashboard={() => setCurrentView("dashboard")}
                  theme={theme}
                  userProfile={userProfile}
                />
              </motion.div>
            )}
            
            {currentView === "dashboard" && (
              <motion.div
                key="dashboard"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Dashboard 
                  purchases={purchaseHistory} 
                  clearHistory={clearHistory}
                  persona={getImpulsePersona()}
                  goToInput={() => setCurrentView("input")}
                  theme={theme}
                  setCurrentView={setCurrentView}
                  userProfile={userProfile}
                />
              </motion.div>
            )}
            
            {currentView === "goals" && (
              <motion.div
                key="goals"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <SpendingGoals 
                  purchaseHistory={purchaseHistory}
                  theme={theme}
                  userProfile={userProfile}
                />
                
                <div className="flex space-x-3 justify-center mt-4">
                  <motion.button
                    onClick={() => setCurrentView("dashboard")}
                    className="px-6 py-3 rounded-xl border border-gray-300 dark:border-gray-600 font-semibold"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Back to Dashboard
                  </motion.button>
                </div>
              </motion.div>
            )}
            
            {currentView === "insights" && (
              <motion.div
                key="insights"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ShoppingInsights 
                  purchases={purchaseHistory}
                  theme={theme}
                  userProfile={userProfile}
                />
                
                <div className="flex space-x-3 justify-center mt-4">
                  <motion.button
                    onClick={() => setCurrentView("dashboard")}
                    className="px-6 py-3 rounded-xl border border-gray-300 dark:border-gray-600 font-semibold"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Back to Dashboard
                  </motion.button>
                </div>
              </motion.div>
            )}
            
            {currentView === "challenges" && (
              <motion.div
                key="challenges"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Challenges 
                  purchases={purchaseHistory}
                  theme={theme}
                  userProfile={userProfile}
                  setCurrentView={setCurrentView}
                />
                
                <div className="flex space-x-3 justify-center mt-4">
                  <motion.button
                    onClick={() => setCurrentView("dashboard")}
                    className="px-6 py-3 rounded-xl border border-gray-300 dark:border-gray-600 font-semibold"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Back to Dashboard
                  </motion.button>
                </div>
              </motion.div>
            )}

            {currentView === "shareable-infographics" && (
              <motion.div
                key="shareable-infographics"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ShareableInfographics 
                  theme={theme}
                  purchaseData={{
                    item: "Sample Item",
                    amount: 42,
                    guiltScore: 58,
                    mood: "happy",
                    purchaseCount: 12,
                    quizTitle: "Sample Quiz",
                    result: "Mindful Spender",
                    resultEmoji: "🧠",
                    resultDescription: "You think before you buy—most of the time."
                  }}
                  userPersonality={"Mindful Spender"}
                />

                <div className="flex space-x-3 justify-center mt-4">
                  <motion.button
                    onClick={() => setCurrentView("dashboard")}
                    className="px-6 py-3 rounded-xl border border-gray-300 dark:border-gray-600 font-semibold"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Back to Dashboard
                  </motion.button>
                </div>
              </motion.div>
            )}

            {currentView === "buy-now-pay-later" && (
              <motion.div
                key="buy-now-pay-later"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <BNPLLandingPage 
                  goToInput={() => setCurrentView("input")} 
                  theme={theme}
                />
              </motion.div>
            )}
            
            {/* Shop View with error handling */}
            {currentView === "shop" && (
              <motion.div
                key="shop"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <React.Suspense fallback={
                  <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8 text-center">
                    <div className="animate-spin text-4xl mb-4">🔄</div>
                    <h2 className="text-xl font-bold dark:text-white">Loading shop recommendations...</h2>
                  </div>
                }>
                  <MindfulShopComponent theme={theme} />
                </React.Suspense>
                
                <div className="flex space-x-3 justify-center mt-4">
                  <motion.button
                    onClick={() => setCurrentView("dashboard")}
                    className="px-6 py-3 rounded-xl border border-gray-300 dark:border-gray-600 font-semibold"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Back to Dashboard
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Related content section */}
          <LazyComponent 
            component={RelatedContent} 
            fallback={null}
            currentView={currentView}
            setCurrentView={setCurrentView}
            theme={theme}
          />
        </main>
      
        <Footer recentGuiltScore={recentGuiltScore} />
        <ToastContainer />
      
        {/* User Profile Modal */}
        {showProfileModal && (
          <UserProfile 
            userProfile={userProfile}
            updateUserProfile={updateUserProfile}
            theme={theme}
            onClose={() => setShowProfileModal(false)}
          />
        )}
      </div>
    </LoadingProvider>
  );
};

export default App;
